import React, { useState, useEffect } from 'react';
import { Copy, Shield, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export function PasswordGenerator() {
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(12);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSpecial, setIncludeSpecial] = useState(false);
  const [copied, setCopied] = useState(false);
  const [strength, setStrength] = useState('weak');

  const generatePassword = () => {
    let charset = '';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) charset += '0123456789';
    if (includeSpecial) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (charset === '') {
      setPassword('');
      return;
    }

    let newPassword = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      newPassword += charset[randomIndex];
    }
    setPassword(newPassword);
  };

  const calculateStrength = () => {
    let score = 0;
    if (password.length >= 12) score += 2;
    else if (password.length >= 8) score += 1;
    
    if (includeUppercase) score += 1;
    if (includeLowercase) score += 1;
    if (includeNumbers) score += 1;
    if (includeSpecial) score += 2;

    if (score >= 6) return 'very strong';
    if (score >= 5) return 'strong';
    if (score >= 4) return 'good';
    if (score >= 2) return 'weak';
    return 'very weak';
  };

  useEffect(() => {
    generatePassword();
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSpecial]);

  useEffect(() => {
    setStrength(calculateStrength());
  }, [password]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const strengthColors = {
    'very weak': 'bg-red-500',
    'weak': 'bg-orange-500',
    'good': 'bg-yellow-500',
    'strong': 'bg-green-500',
    'very strong': 'bg-emerald-500'
  };

  return (
    <>
      <Helmet>
        <title>Strong Password Generator | DigiVault.club</title>
        <meta name="description" content="Generate secure, random passwords with our free password generator. Create strong passwords with uppercase, lowercase, numbers, and special characters." />
        <meta name="keywords" content="password generator, strong password, random password, secure password, password security" />
        <link rel="canonical" href="https://digivault.club/password-generator" />
      </Helmet>
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Shield className="w-8 h-8 text-emerald-500" />
              <h1 className="text-3xl font-bold text-white">Strong Password Generator</h1>
            </div>

            <div className="bg-gray-800 rounded-xl p-6 shadow-xl mb-8">
              <div className="flex items-center gap-4 mb-6">
                <input
                  type="text"
                  value={password}
                  readOnly
                  className="flex-1 bg-gray-700 rounded-lg px-4 py-3 font-mono text-lg text-white"
                />
                <button
                  onClick={copyToClipboard}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-lg px-4 py-3 transition-colors"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>

              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-white">Password Length: {length}</span>
                  <span className="capitalize text-white">{strength}</span>
                </div>
                <input
                  type="range"
                  min="6"
                  max="32"
                  value={length}
                  onChange={(e) => setLength(parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="h-2 mt-2 rounded-full bg-gray-700 overflow-hidden">
                  <div 
                    className={`h-full transition-all ${strengthColors[strength]}`}
                    style={{ width: `${(strength === 'very strong' ? 100 : strength === 'strong' ? 80 : strength === 'good' ? 60 : strength === 'weak' ? 40 : 20)}%` }}
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeUppercase}
                    onChange={(e) => setIncludeUppercase(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-white">Uppercase (ABC)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeLowercase}
                    onChange={(e) => setIncludeLowercase(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-white">Lowercase (abc)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={(e) => setIncludeNumbers(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-white">Numbers (123)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeSpecial}
                    onChange={(e) => setIncludeSpecial(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-white">Special Characters (!@#)</span>
                </label>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-6 shadow-xl">
              <h2 className="text-2xl font-bold mb-4 text-white">Why Use a Strong Password?</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Strong passwords are your first line of defense against unauthorized access to your accounts.
                  A strong password should:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Be at least 12 characters long</li>
                  <li>Include a mix of uppercase and lowercase letters</li>
                  <li>Contain numbers and special characters</li>
                  <li>Be unique for each account you have</li>
                  <li>Avoid personal information or common words</li>
                </ul>
                <p>
                  Using a strong password significantly reduces the risk of your accounts being compromised
                  through brute force attacks or dictionary-based password cracking attempts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}